import React, { Component, ErrorInfo, ReactNode } from 'react';
import { RefreshCw, AlertTriangle, ShieldCheck } from 'lucide-react';

interface Props {
  children?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught React Error caught by ErrorBoundary:', error, errorInfo);
  }

  private handleReload = () => {
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  };

  private handleClearAndReload = () => {
    if (typeof window !== 'undefined') {
      try {
        localStorage.removeItem('rh_cached_products');
        localStorage.removeItem('rh_orders_cache');
        localStorage.removeItem('rh_categories_cache');
        localStorage.removeItem('rh_downloads_cache');
      } catch (e) {}
      window.location.reload();
    }
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col items-center justify-center p-6 font-sans">
          <div className="max-w-md w-full p-8 rounded-3xl bg-slate-900/90 border border-rose-500/30 shadow-2xl text-center space-y-6">
            <div className="w-16 h-16 rounded-2xl bg-rose-500/20 border border-rose-500/40 flex items-center justify-center mx-auto text-rose-400 font-black shadow-lg">
              <AlertTriangle className="w-8 h-8 text-rose-400" />
            </div>

            <div className="space-y-2">
              <h1 className="text-xl font-black text-white tracking-wide">
                Application Recovered
              </h1>
              <p className="text-xs text-rose-400 font-mono font-bold break-words px-2">
                {this.state.error?.message || 'An unexpected runtime issue was intercepted.'}
              </p>
            </div>

            <p className="text-xs text-slate-400 leading-relaxed">
              The application encountered a client runtime issue. You can reload or reset local state below.
            </p>

            <div className="space-y-2">
              <button
                onClick={this.handleReload}
                className="w-full py-3 px-6 rounded-2xl bg-blue-600 hover:bg-blue-500 text-white font-extrabold text-sm flex items-center justify-center gap-2 shadow-lg shadow-blue-500/20 transition-all active:scale-95 cursor-pointer"
              >
                <RefreshCw className="w-4 h-4" />
                <span>Reload Page</span>
              </button>

              <button
                onClick={this.handleClearAndReload}
                className="w-full py-2.5 px-6 rounded-2xl bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold text-xs flex items-center justify-center gap-2 border border-slate-700 transition-all active:scale-95 cursor-pointer"
              >
                <span>Clear Cache & Clean Reload</span>
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
